<template>
  <div class="flex-col w-full h-full bg-white rounded-xl p-4">
    <div class="flex items-center justify-between mb-4">
      <div class="flex flex-wrap gap-x-4 gap-y-2">
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.batch_id") + ":"
          }}</span>
          <span class="text-primary">{{ batchDetail?.batch_id }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.organization") + ":"
          }}</span>
          <span>{{ batchDetail?.organisation }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.package") + ":"
          }}</span>
          <span>{{ translatedPackages }}</span>
        </div>
        <div class="flex items-center gap-2">
          <span class="font-semibold">{{
            $t("pages.batch_processing_table.entity") + ":"
          }}</span>
          <span>{{ translatedEntityType }}</span>
        </div>
      </div>
      <div class="flex items-center">
        <div class="flex flex-wrap gap-4">
          <div class="flex items-center gap-2" v-show="isEditPermission">
            <Button
              :text="$t(`pages.batch_processing_table.add_row`)"
              type="primary"
              outline
              @click="addRow"
            />
          </div>
          <div class="flex items-center gap-2" v-show="isEditPermission">
            <Button
              :text="$t(`pages.batch_processing_table.copy_grid`)"
              type="primary"
              outline
              @click="copyGrid"
            />
          </div>
          <div class="flex items-center gap-2" v-show="isEditPermission">
            <Button
              :text="$t(`pages.batch_processing_table.clear_grid`)"
              @click="clearGrid"
              type="primary"
              outline
            />
          </div>
          <div class="flex items-center gap-2">
            <Button
              :text="$t(`pages.batch_processing_table.download_xlsx`)"
              type="primary"
              outline
              @click="downloadXLSX"
            />
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="batchDetailLoading"
      class="flex items-center justify-center h-96"
    >
      <!-- <span class="text-gray-500">{{ $t("general.loading") }}</span> -->
      <Loader />
    </div>
    <div v-else style="max-width: 100%; overflow-x: auto">
      <div
        ref="tabulatorRef"
        style="margin-bottom: 18px; margin-top: 18px; min-width: 600px"
      ></div>
    </div>

    <div class="flex items-center justify-between mt-4">
      <div class="flex flex-wrap gap-2">
        <span class="italic">
          {{ $t(`pages.batch_processing_table.tip_use`) }}
          <span class="font-bold">{{
            $t(`pages.batch_processing_table.ctrl_c_ctrl_c`)
          }}</span>
          <span class="font-bold">{{
            $t(`pages.batch_processing_table.to_copy_paste`)
          }}</span>
          {{ $t(`pages.batch_processing_table.rows_with`) }}
          <span class="font-bold">{{
            $t(`pages.batch_processing_table.excel_sheet_this`)
          }}</span>
          {{ $t(`pages.batch_processing_table.grid`) }}
        </span>
      </div>
      <div class="flex items-center gap-4" v-show="isEditPermission">
        <div class="flex items-center gap-2">
          <Button
            @click="handleSave"
            :text="$t(`actions.save`)"
            type="primary"
            :disabled="!anyRowExist"
            :loader="saveLoader"
          />
        </div>
        <div class="flex items-center gap-2">
          <Button
            @click="submitTheData"
            :text="$t(`actions.submit`)"
            type="success"
            :disabled="!anyRowExist"
            :disable="anyRowExist"
            :loader="submitLoadig"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable no-irregular-whitespace */
import * as XLSX from "xlsx";
import Button from "@/components/button";
import BatchAxios from "../batch-axios";
import { checkPermission } from "@shared/utils/functions";
import { EventBus } from "@/main.js";
// import { sampleData } from "./newData1";
import Loader from "@shared/loader";
import { translateEntity } from "@shared/modules/localization/utils/caseEntityTranslation"
import { caseEntityTypesMap } from "@shared/modules/localization/utils/caseEntityTranslation/constants";


window.XLSX = XLSX;

export default {
  name: "TabulatorGrid",
  data() {
    return {
      lang: "en",
      table: null,
      batchDetailLoading: false,
      editingRowIndex: null,
      rowData: [],
      columnMeta: [],
      saveLoader: false,
      submitLoadig: false,
      editableIfDependencyMap: {},
      columns: [],
      addedFields: new Set(),
      columnGenerating: false,
      maxRowLimit: 10000, // Set a maximum row limit
    };
  },
  props: {
    batchDetail: {
      type: Object,
      default: () => {},
    },
  },
  computed: {
    translatedPackages() {
        return translateEntity(this.batchDetail, caseEntityTypesMap.PACKAGE)?.package_name;
    },
    translatedEntityType() {
        return translateEntity(this.batchDetail, caseEntityTypesMap.ENTITY_TYPE)?.entity_type;
    },
    getAction() {
      return this.$route.params.action;
    },
    getBatchId() {
      return this.$route.params.id;
    },
    anyRowExist() {
      return this.table?.getDataCount() > 0 ?? false;
    },
    isEditPermission() {
      return (
        this.$route.params.action === "edit" &&
        (checkPermission("batchProcessing.edit") || checkPermission("batchProcessing.create"))
      );
    },
    isMaxRowLimitReached() {
      return this.table?.getDataCount() >= this.maxRowLimit;
    },
  },
  components: {
    Button,
    Loader,
  },
  async mounted() {
    await this.fetchBatchTabularData();
    this.$nextTick(() => {
      this.initGrid();
    });
    EventBus.$on("changeLanguageProfile", () => {
      this.refreshTheGrid();
    })
  },
  beforeDestroy() {
    EventBus.$off("changeLanguageProfile");
  },
  methods: {
    checkPermission,
    translateEntity,
    caseEntityTypesMap,

    // Universal helper methods for working with field hierarchies
    
    // Find active field for groupKey based on current row values
    findActiveFieldForGroupKey(groupKey, rowData) {
      const fieldsWithGroupKey = this.columnMeta.filter(c => c.groupKey === groupKey);
      
      const activeField = fieldsWithGroupKey.find(field => {
        const isActive = this.isFieldActive(field, rowData);
        return isActive;
      });
      
      return activeField || null;
    },

    // Check if field is active based on its conditions
    isFieldActive(field, rowData, visitedFields = new Set()) {
      const fieldKey = field.field || field.groupKey;
      
      // Prevent infinite loops by tracking visited fields
      if (visitedFields.has(fieldKey)) {
        console.warn(`Circular dependency detected in isFieldActive for field: ${fieldKey}`);
        return false;
      }
      visitedFields.add(fieldKey);

      // Check editableIf condition
      if (field.editableIf) {
        const parentValue = rowData[field.editableIf.field];
        if (parentValue !== field.editableIf.value) {
          return false;
        }
      }

      // Check parentField/parentValue condition
      if (field.parentField && field.parentValue !== undefined) {
        const parentFieldValue = this.getFieldValue(field.parentField, rowData);
        if (parentFieldValue !== field.parentValue) {
          return false;
        }
        
        // Additional check: ensure parent field itself is active (for deep hierarchies)
        const parentFieldMeta = this.columnMeta.find(c => c.field === field.parentField || c.groupKey === field.parentField);
        if (parentFieldMeta && (parentFieldMeta.parentField || parentFieldMeta.editableIf)) {
          if (!this.isFieldActive(parentFieldMeta, rowData, visitedFields)) {
            return false;
          }
        }
      }

      return true;
    },

    // Get field value from row data (considering groupKey)
    getFieldValue(fieldName, rowData, visitedFields = new Set()) {
      // Prevent infinite loops by tracking visited fields
      if (visitedFields.has(fieldName)) {
        console.warn(`Circular dependency detected in getFieldValue for field: ${fieldName}`);
        return undefined;
      }
      visitedFields.add(fieldName);

      // First check if there's a groupKey for this field
      const fieldWithGroupKey = this.columnMeta.find(c => 
        c.field === fieldName && c.groupKey
      );
      
      if (fieldWithGroupKey) {
        // For groupKey fields, we need to find the active field and get its value
        const activeField = this.findActiveFieldForGroupKey(fieldWithGroupKey.groupKey, rowData);
        if (activeField) {
          const value = rowData[activeField.field];
          return value;
        }
        // Fallback to groupKey field value (from prepareDataForDisplay)
        const fallbackValue = rowData[fieldWithGroupKey.groupKey];
        return fallbackValue;
      }
      
      // Check if fieldName itself is a groupKey
      const groupKeyFields = this.columnMeta.filter(c => c.groupKey === fieldName);
      if (groupKeyFields.length > 0) {
        const activeField = this.findActiveFieldForGroupKey(fieldName, rowData);
        if (activeField) {
          const value = rowData[activeField.field];
          return value;
        }
        // Fallback to groupKey field value
        const fallbackValue = rowData[fieldName];
        return fallbackValue;
      }
      
      const directValue = rowData[fieldName];
      return directValue;
    },

    // Find all dependent fields for given parent field (with caching)
    getDependentFields(parentFieldName) {
      // Simple caching to avoid repeated filtering
      const cacheKey = `deps_${parentFieldName}`;
      if (this._dependencyCache && this._dependencyCache[cacheKey]) {
        return this._dependencyCache[cacheKey];
      }

      const dependents = [];
      
      // Dependencies through editableIf
      const editableIfDependents = this.columnMeta.filter(col => 
        col.editableIf && col.editableIf.field === parentFieldName
      );
      dependents.push(...editableIfDependents);

      // Dependencies through parentField
      const parentFieldDependents = this.columnMeta.filter(col => 
        col.parentField === parentFieldName
      );
      dependents.push(...parentFieldDependents);

      // Additional check: if parentFieldName is a groupKey, also check dependencies 
      // for all specific fields in that groupKey
      const fieldsInGroup = this.columnMeta.filter(col => col.groupKey === parentFieldName);
      fieldsInGroup.forEach(fieldInGroup => {
        // Find dependencies for specific field names within the group
        const specificFieldDependents = this.columnMeta.filter(col => 
          (col.editableIf && col.editableIf.field === fieldInGroup.field) ||
          (col.parentField === fieldInGroup.field)
        );
        dependents.push(...specificFieldDependents);
      });

      // Cache result
      if (!this._dependencyCache) {
        this._dependencyCache = {};
      }
      this._dependencyCache[cacheKey] = dependents;

      return dependents;
    },

    // Get editor parameters for field with groupKey
    getEditorParams(col, cell) {
      if (!col.groupKey) {
        return col.editorParams || {};
      }

      const row = cell.getRow().getData();
      
      // For groupKey fields, collect all possible values from all fields with this groupKey
      // that match current row context
      const fieldsWithGroupKey = this.columnMeta.filter(c => c.groupKey === col.groupKey);
      const allValues = [];
      const seenValues = new Set(); // Track unique values by id or value
      let isMultiselect = false; // Check if any of the active fields is multiselect
      
      fieldsWithGroupKey.forEach(field => {
        // Check if this field is potentially active for current row
        if (this.isFieldActive(field, row)) {
          // Check if this field is multiselect
          if (field.editorParams?.multiselect) {
            isMultiselect = true;
          }
          
          if (field.editorParams && field.editorParams.values) {
            field.editorParams.values.forEach(value => {
              const valueKey = value.id || value.value;
              if (!seenValues.has(valueKey)) {
                seenValues.add(valueKey);
                allValues.push(value);
              }
            });
          }
        }
      });
      
      // If we found values, return them with multiselect flag
      if (allValues.length > 0) {
        return { 
          values: allValues,
          ...(isMultiselect && { multiselect: true })
        };
      }

      // Fallback to active field or column params
      const activeField = this.findActiveFieldForGroupKey(col.groupKey, row);
      if (activeField && activeField.editorParams) {
        return activeField.editorParams;
      }

      // Final fallback
      const params = col.editorParams || { values: [] };
      if (!params.values || !Array.isArray(params.values)) {
        return { values: [] };
      }
      return params;
    },

    // Universal formatting for list values
    formatListValue(cell, col) {
      const value = cell.getValue();
      if (!value) return value;

      // Handle multiselect arrays
      if (Array.isArray(value)) {
        let editorParams = col.editorParams;
        if (typeof editorParams === 'function') {
          editorParams = editorParams(cell);
        }
        if (editorParams?.values) {
          const labels = value.map(val => {
            const option = editorParams.values.find(opt => opt.value === val);
            return option ? option.label : val;
          });
          return labels.join(', ');
        }
        return value.join(', ');
      }

      // For single values
      let editorParams = col.editorParams;
      if (typeof editorParams === 'function') {
        editorParams = editorParams(cell);
      }
      if (editorParams?.values) {
        const option = editorParams.values.find(opt => opt.value === value);
        return option ? option.label : value;
      }
      
      return value;
    },

    // Format value for groupKey fields - find active field and get its value
    formatGroupKeyValue(cell, col) {
      const row = cell.getRow().getData();
      const activeField = this.findActiveFieldForGroupKey(col.groupKey, row);
      
      if (!activeField) {
        return ""; // No active field for current row context
      }
      
      // Try to get value from the groupKey field first (prepared data), then from actual field
      let value = row[col.groupKey];
      if (value === undefined || value === null) {
        value = row[activeField.field];
      }
      
      if (!value) return "";
      
      // Handle multiselect arrays for groupKey fields
      if (Array.isArray(value)) {
        if (activeField.editor === "list" && activeField.editorParams?.values) {
          const labels = value.map(val => {
            const option = activeField.editorParams.values.find(opt => opt.value === val);
            return option ? option.label : val;
          });
          return labels.join(', ');
        }
        return value.join(', ');
      }
      
      // If it's a list field, format using the active field's editorParams
      if (activeField.editor === "list" && activeField.editorParams?.values) {
        const option = activeField.editorParams.values.find(opt => opt.value === value);
        return option ? option.label : value;
      }
      
      return value;
    },

    // Check if field is a multiselect field
    isMultiselectField(col) {
      if (col.groupKey) {
        // For groupKey fields, check if any of the grouped fields is multiselect
        const fieldsWithGroupKey = this.columnMeta.filter(c => c.groupKey === col.groupKey);
        return fieldsWithGroupKey.some(field => 
          field.editor === "list" && field.editorParams?.multiselect
        );
      }
      
      // For regular fields
      return col.editor === "list" && col.editorParams?.multiselect;
    },

    // Universal check for field editability
    isFieldEditable(cell, col) {
      const row = cell.getRow().getData();
      
      if (col.groupKey) {
        // For groupKey fields check if there's an active field
        const activeField = this.findActiveFieldForGroupKey(col.groupKey, row);
        return !!activeField;
      }

      // For regular fields check editableIf conditions
      if (col.editableIf) {
        const parentValue = row[col.editableIf.field];
        return parentValue === col.editableIf.value;
      }

      return true;
    },

    async fetchBatchTabularData() {
      try {
        this.batchDetailLoading = true;
        const { data } = await BatchAxios.get(
          `/batch-processing/get-batch-form-fields/${this.getBatchId}?use_groupkey=true`
        );
        this.columnMeta = data.headers
          // Filters for excluding specific fields
          // .filter(col => 
          //   !(col.field === 'tpi/asp_or_non_tpi' || 
          //     col.field === 'can_you_provide_proof_of_establishment?' || 
          //     col.parentField === 'tpi/asp_or_non_tpi')
          // );
        this.rowData = data.data;
        
        // Clear dependency cache when data changes
        this._dependencyCache = null;
        
        // this.columnMeta = sampleData.headers;
        // this.rowData = sampleData.data;
        this.batchDetailLoading = false;
      } catch (error) {
        console.error("err", error);
        this.$toast.error(
          error?.response?.data?.detail || this.$t(`general.default_error`)
        );
        this.batchDetailLoading = false;
      }
    },
    handleReset(cell) {
      const field = cell.getField();
      const row = cell.getRow();

      // Collect all updates in a single object to apply them in one batch
      const allUpdates = {};
      const cellsToClear = [];

      // Recursive function to collect field resets
      const collectResets = (fieldName, visitedFields = new Set()) => {
        // Prevent infinite loops
        if (visitedFields.has(fieldName)) return;
        visitedFields.add(fieldName);

        // Find dependent fields for current field
        const dependentFields = this.getDependentFields(fieldName);
        
        dependentFields.forEach((col) => {
          const fieldToReset = col.groupKey || col.field;
          
          // Collect reset values
          allUpdates[fieldToReset] = "";
          
          // Also reset in specific field if it's a groupKey
          if (col.groupKey && col.field !== col.groupKey) {
            allUpdates[col.field] = "";
          }
          
          // Collect cells to clear edited state
          cellsToClear.push(fieldToReset);

          // Recursively collect resets for dependencies of this field
          collectResets(fieldToReset, visitedFields);
        });
      };

      // Collect all resets
      collectResets(field);

      // Apply all updates in one batch if there are any
      if (Object.keys(allUpdates).length > 0) {
        row.update(allUpdates);
        
        // Clear edited state for all affected cells
        cellsToClear.forEach(fieldToReset => {
          try {
            const childCell = row.getCell(fieldToReset);
            if (childCell) childCell.clearEdited();
          } catch (e) {
            // Ignore errors for non-existent cells
          }
        });
      }
    },

    generateColumns() {
      try {
        this.columnGenerating = true;
        // Reset before each generation
        this.columns = [];
        this.addedFields = new Set();

        // Find all root fields (without parentField)
        const roots = (this.columnMeta || []).filter(f => !f.parentField);

        // Build column tree from root elements
        roots.forEach((root) => this.buildCol(root, this, new Set()));

        // Add standalone fields that didn't fall into hierarchy
        (this.columnMeta || []).forEach((col) => {
          if (!this.addedFields.has(col.field)) {
            this.buildCol(col, this, new Set());
          }
        });

        // Add Actions column (at the end, for edit mode)
        if (this.isEditPermission) {
          this.columns.push({
            title: this.$t("general.actions"),
            field: "actions",
            formatter: () => "🗑️",
            width: 70,
            minWidth: 70,
            headerTooltip: "Delete row",
            headerSort: false,
            cellClick: (e, cell) => cell.getRow().delete(),
          });
        }
        return this.columns;
      } catch (err) {
        console.error("error in generateColumns", err);
        return [];
      } finally {
        this.columnGenerating = false;
      }
    },

    // Universal recursive function for building columns
    buildCol(col, context, visitedFields = new Set()) {
      if (!col) return;

      // Prevent infinite loops by tracking visited fields
      if (visitedFields.has(col.field)) {
        console.warn(`Circular dependency detected for field: ${col.field}`);
        return;
      }
      visitedFields.add(col.field);

      // Create unique key for this column - use groupKey directly to avoid duplicates
      const uniqueKey = col.groupKey || col.field;

      // Skip if already added
      if (this.addedFields.has(uniqueKey)) return;
      this.addedFields.add(uniqueKey);

      let baseTitle = col.titles || col.field || "";
      if (baseTitle.length > 50) {
        baseTitle = baseTitle.substring(0, 50) + "...";
      }
      const title = col.required
        ? `${baseTitle}<span class="tabulator-custom-required-asterisk px-1"> * </span>`
        : baseTitle;

      // Use groupKey as field name for groupKey columns
      const fieldToUse = col.groupKey || col.field;

      const def = {
        ...col,
        field: fieldToUse,
        title,
        minWidth: 150,
        headerTooltip: col.titles || col.field,
        headerSort: true,
        editor: col.editor || "input",
        editorParams: col.groupKey 
          ? (cell) => this.getEditorParams(col, cell)
          : col.editorParams || {},
        formatter: col.groupKey
          ? (cell) => this.formatGroupKeyValue(cell, col)
          : (col.editor === "list" 
            ? (cell) => this.formatListValue(cell, col)
            : undefined),
        editable: context.isEditPermission
          ? (cell) => this.isFieldEditable(cell, col)
          : false,
      };

      this.columns.push(def);

      // Find children (any level) through parentField
      const children = (context.columnMeta || []).filter(
        (f) => f.parentField === col.field
      );
      children.forEach((childCol) => this.buildCol(childCol, context, visitedFields));
    },
    
    // Prepare data for display by copying values from active fields to groupKey fields
    prepareDataForDisplay(data) {
      return data.map(row => {
        const preparedRow = { ...row };
        
        // Find all unique groupKeys
        const groupKeys = [...new Set(this.columnMeta.filter(c => c.groupKey).map(c => c.groupKey))];
        
        groupKeys.forEach(groupKey => {
          const activeField = this.findActiveFieldForGroupKey(groupKey, row);
          
          if (activeField) {
            // If the active field has a value, use it
            if (row[activeField.field] !== undefined && row[activeField.field] !== '') {
              preparedRow[groupKey] = row[activeField.field];
            } 
            // If the active field is empty but groupKey has a value (from server), 
            // copy groupKey value to the active field
            else if (row[groupKey] !== undefined && row[groupKey] !== '') {
              preparedRow[activeField.field] = row[groupKey];
              preparedRow[groupKey] = row[groupKey];
            }
          } else if (row[groupKey] !== undefined && row[groupKey] !== '') {
            // If no active field found but groupKey has value, keep the groupKey value
            // This can happen during initialization before hierarchy is properly resolved
            preparedRow[groupKey] = row[groupKey];
          }
        });
        
        return preparedRow;
      });
    },
    
    initGrid() {
      const columns = this.generateColumns() || [];
      const rawData = this.table
        ? this.table.getData()
        : JSON.parse(JSON.stringify(this.rowData));
      const data = this.prepareDataForDisplay(rawData);
      const gridElement = this.$refs.tabulatorRef;
      if (!gridElement) {
        console.error("Tabulator init error: $refs.grid is undefined");
        return;
      }
      this.table = new window.Tabulator(gridElement, {
        height: "420px",
        layout: "fitColumns",
        responsiveLayout: false,
        clipboard: this.isEditPermission,
        rowHeader: {
          formatter: "rownum",
          headerSort: false,
          hozAlign: "center",
          resizable: false,
          frozen: true,
          width: 50,
          minWidth: 40,
        },
        columns,
        data,
        ...(this.isEditPermission && {
          clipboardPasteParser: (clipboard) => {
            const rows = clipboard
              .trim()
              .split("\n")
              .map((row) => row.split("\t"));
            const allColumns = this.table.getColumns();
            const fields = allColumns.map((col) => col.getField());

            const dataFields = fields.filter(
              (f) => f && f !== "actions" && f !== "id"
            );

            // Check if first row contains headers by comparing with column titles
            const columnTitles = allColumns
              .map((col) => {
                const title = col.getDefinition().title;
                return typeof title === "string"
                  ? title
                      .replace(/<[^>]*>/g, "")
                      .replace(/\*/g, "")
                      .trim()
                      .toLowerCase()
                  : "";
              })
              .filter((t) => t);

            let dataRows = rows;
            let headerMapping = null;
            
            if (rows.length > 0) {
              const firstRowLower = rows[0].map(
                (cell) => cell?.trim().replace(/\*+$/, '').trim().toLowerCase() || ""
              );
              
              const hasHeaders = firstRowLower.some((cell) =>
                columnTitles.includes(cell)
              );
              
              if (hasHeaders) {
                // Create mapping from header names to field names
                headerMapping = {};
                firstRowLower.forEach((headerName, index) => {
                  // Find the corresponding field name for this header
                  const column = allColumns.find(col => {
                    const colTitle = col.getDefinition().title;
                    const cleanTitle = typeof colTitle === "string"
                      ? colTitle
                          .replace(/<[^>]*>/g, "")
                          .replace(/\*/g, "")
                          .trim()
                          .toLowerCase()
                      : "";
                    return cleanTitle === headerName;
                  });
                  
                  if (column) {
                    headerMapping[index] = column.getField();
                  }
                });
                
                dataRows = rows.slice(1);
              }
            }

            const existingIds = this.rowData.map((r) => r.id);
            let nextId = existingIds.length ? Math.max(...existingIds) + 1 : 1;

            const newRows = dataRows
              .filter((row) => row.some(cell => cell && cell.trim() !== "")) // Filter empty rows first
              .map((row) => {
                const obj = { id: nextId++ };
                
                if (headerMapping) {
                  // Use header mapping to assign values to correct fields
                  Object.entries(headerMapping).forEach(([index, fieldName]) => {
                    let value = row[index]?.trim() || "";
                    
                    // Check if value looks like a JSON array string
                    if (value.startsWith('[') && value.endsWith(']')) {
                      try {
                        value = JSON.parse(value);
                      } catch (e) {
                        // Keep as string if JSON parsing fails
                        console.error(`Failed to parse JSON array for field ${fieldName}:`, e);
                      }
                    }
                    
                    obj[fieldName] = value;
                  });
                } else {
                  // Fallback to original logic if no headers detected
                  dataFields.forEach((field, i) => {
                    let value = row[i]?.trim() || "";
                    
                    // Check if value looks like a JSON array string
                    if (value.startsWith('[') && value.endsWith(']')) {
                      try {
                        value = JSON.parse(value);
                      } catch (e) {
                        // Keep as string if JSON parsing fails
                        console.error(`Failed to parse JSON array for field ${field}:`, e);
                      }
                    }
                    
                    obj[field] = value;
                  });
                }
                
                // First, process all regular fields (non-groupKey) to establish parent values
                Object.keys(obj).forEach(fieldName => {
                  if (fieldName === 'id') return; // Skip ID field
                  
                  // Check if this is a regular field (not groupKey)
                  const fieldMeta = this.columnMeta.find(meta => meta.field === fieldName);
                  if (fieldMeta && !fieldMeta.groupKey && obj[fieldName] && obj[fieldName] !== '') {
                    // Try to map the value to a correct system value
                    let mappedValue = this.mapExcelValueToSystemValue(fieldName, obj[fieldName], obj);
                    if (mappedValue !== obj[fieldName]) {
                      obj[fieldName] = mappedValue;
                    }
                  }
                });
                
                // Process groupKey fields after all data is set
                const groupKeys = [...new Set(this.columnMeta.filter(c => c.groupKey).map(c => c.groupKey))];
                
                // Sort groupKeys to process parent fields first (fields without dependencies)
                const sortedGroupKeys = this.sortGroupKeysByDependencies(groupKeys);
                
                sortedGroupKeys.forEach(groupKey => {
                  if (obj[groupKey] && obj[groupKey] !== '') {
                    // First, try to map the value to a correct system value
                    let mappedValue = this.mapExcelValueToSystemValue(groupKey, obj[groupKey], obj);
                    if (mappedValue !== obj[groupKey]) {
                      obj[groupKey] = mappedValue;
                    }
                    
                    // Find the active field for this groupKey
                    const activeField = this.findActiveFieldForGroupKey(groupKey, obj);
                    
                    if (activeField) {
                      let valueToSet = obj[groupKey];
                      
                      // Handle multiselect fields - convert string to array if needed
                      if (activeField.editorParams?.multiselect && typeof valueToSet === 'string') {
                        // Get dynamic editor params for this specific row context
                        const dynamicParams = typeof activeField.editorParams === 'function' 
                          ? activeField.editorParams({ getRow: () => ({ getData: () => obj }) })
                          : activeField.editorParams;
                        
                        const availableValues = dynamicParams?.values || [];
                        
                        // Try different matching strategies
                        let matchingOption = null;
                        
                        // 1. Exact label match
                        matchingOption = availableValues.find(option => 
                          option.label === valueToSet
                        );
                        
                        // 2. Exact value match
                        if (!matchingOption) {
                          matchingOption = availableValues.find(option => 
                            option.value === valueToSet
                          );
                        }
                        
                        // 3. Case-insensitive label match
                        if (!matchingOption) {
                          matchingOption = availableValues.find(option => 
                            option.label?.toLowerCase() === valueToSet.toLowerCase()
                          );
                        }
                        
                        // 4. Partial match (if the pasted text contains the option label)
                        if (!matchingOption) {
                          matchingOption = availableValues.find(option => 
                            valueToSet.includes(option.label) || option.label?.includes(valueToSet)
                          );
                        }
                        
                        if (matchingOption) {
                          valueToSet = [matchingOption.value];
                        }
                        // Else keep original value as string, maybe it's a custom value
                      }
                      
                      obj[activeField.field] = valueToSet;
                    } else {
                      // If no active field found, try to find any field with this groupKey as fallback
                      const fieldsWithGroupKey = this.columnMeta.filter(c => c.groupKey === groupKey);
                      if (fieldsWithGroupKey.length) {
                        obj[fieldsWithGroupKey[0].field] = obj[groupKey];
                      }
                    }
                  }
                });
                
                return obj;
              })
              .filter((row) => {
                const values = Object.values(row).filter(val => val !== row.id);
                return values.some(val => val && val.toString().trim() !== "");
              });

            // Check if pasting would exceed row limit
            const currentRowCount = this.table.getDataCount();
            if (currentRowCount + newRows.length > this.maxRowLimit) {
              this.$toast.error(
                this.$t("pages.batch_processing_table.max_limit_reached")
              );
              return [];
            }

            this.rowData.push(...newRows);
            return newRows;
          },
        }),
      });

      // Add CSS class for view mode
      if (!this.isEditPermission) {
        gridElement.classList.add("view-mode");
      }

      // Initialize groupKey field values properly after table creation
      this.initializeGroupKeyFields();

      this.table.on("cellEdited", (cell) => {
        const value = cell.getValue();
        const field = cell.getField();
        const row = cell.getRow().getData();
        
        // Handle groupKey fields - redirect value to active field
        const groupKeyField = this.columnMeta.find(meta => meta.groupKey === field);
        if (groupKeyField) {
          const activeField = this.findActiveFieldForGroupKey(field, row);
          if (activeField) {
            // Set value in the active field
            row[activeField.field] = value;
            // Also keep the value in groupKey field for consistent access
            row[field] = value;
            // Update the row data
            cell.getRow().update(row);
          }
        }
        
        // Check if this is a multiselect field
        const isMultiselect = this.columnMeta.some(meta => 
          ((meta.groupKey && meta.groupKey === field) || meta.field === field) &&
          meta.editor === "list" && 
          meta.editorParams?.multiselect
        );
        
        if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
          // If value is an object like {"": "Yes"}, extract the actual value
          const keys = Object.keys(value);
          if (keys.length === 1) {
            const actualValue = value[keys[0]];
            if (actualValue && actualValue !== '') {
              cell.setValue(actualValue);
            } else {
              cell.setValue('');
            }
          } else {
            cell.setValue('');
          }
        } else if (isMultiselect && typeof value === 'string' && value.includes(',')) {
          // For multiselect fields, convert comma-separated strings to arrays
          const arrayValue = value.split(',').map(v => v.trim()).filter(v => v !== '');
          cell.setValue(arrayValue);
        }
        // Arrays (multiselect values) are preserved as-is

        this.handleReset(cell);


        // No redraw needed - editor options are calculated dynamically via getEditorParams. Check dependentFields.length if needed:
        // // Refresh dependent fields when parent changes (debounced)
        // let dependentFields = this.getDependentFields(field);
        
        // // For groupKey fields, also check dependencies from the active field
        // const groupKeyMeta = this.columnMeta.find(meta => meta.groupKey === field);
        // if (groupKeyMeta) {
        //   const activeField = this.findActiveFieldForGroupKey(field, row);
        //   if (activeField) {
        //     const activeFieldDependents = this.getDependentFields(activeField.field);
        //     dependentFields = [...dependentFields, ...activeFieldDependents];
        //   }
        // }
      });

      // Add cellEditCancelled handler to preserve array values
      this.table.on("cellEditCancelled", (cell) => {
        const col = cell.getColumn().getDefinition();
        
        // Check if this is a multiselect field
        if (this.isMultiselectField(col)) {
          // Preserve the original array value, don't let it convert to string
          const originalValue = cell.getOldValue();
          if (Array.isArray(originalValue)) {
            cell.restoreOldValue();
          }
        }
      });

      // Add cellEditing handler for proper multiselect initialization
      this.table.on("cellEditing", (cell) => {
        const col = cell.getColumn().getDefinition();
        
        // Check if this is a multiselect field
        const isMultiselect = this.isMultiselectField(col);
        
        if (isMultiselect) {
          // Get the current value
          let currentValue = cell.getValue();
          
          // For multiselect, we need to ensure the value is an array
          if (Array.isArray(currentValue)) {
            // Already an array, keep as is
          } else if (typeof currentValue === 'string' && currentValue.includes(',')) {
            // Convert comma-separated string to array
            const arrayValue = currentValue.split(',').map(v => v.trim()).filter(v => v !== '');
            cell.setValue(arrayValue);
          } else if (typeof currentValue === 'string' && currentValue.trim() !== '') {
            // Single value - convert to array
            const arrayValue = [currentValue.trim()];
            cell.setValue(arrayValue);
          } else {
            // Empty or undefined - set empty array
            cell.setValue([]);
          }
        }
      });

      // this.table.on("clipboardPaste", function(data) {
      //   // Remove any rows that are completely empty
      //   const cleanedData = data.filter(row =>
      //     Object.values(row).some(val => val && val.toString().trim() !== "")
      //   );
      //   this.table.updateData(cleanedData);
      // });
    },

    // Initialize groupKey field values after table creation
    initializeGroupKeyFields() {
      if (!this.table) return;
      
      const rows = this.table.getRows();
      
      rows.forEach(row => {
        const rowData = row.getData();
        const updatedData = { ...rowData };
        let dataChanged = false; // Move inside row loop
        
        // Find all unique groupKeys
        const groupKeys = [...new Set(this.columnMeta.filter(c => c.groupKey).map(c => c.groupKey))];
        
        groupKeys.forEach(groupKey => {
          const activeField = this.findActiveFieldForGroupKey(groupKey, rowData);
          
          if (activeField) {
            // Check if groupKey has a value but active field doesn't
            if (rowData[groupKey] !== undefined && rowData[groupKey] !== '' && 
                (!rowData[activeField.field] || rowData[activeField.field] === '')) {
              updatedData[activeField.field] = rowData[groupKey];
              dataChanged = true;
            }
            // Also ensure groupKey field has the value for display (from active field)
            else if (rowData[activeField.field] !== undefined && rowData[activeField.field] !== '' &&
                     (!rowData[groupKey] || rowData[groupKey] === '')) {
              updatedData[groupKey] = rowData[activeField.field];
              dataChanged = true;
            }
          }
        });
        
        if (dataChanged) {
          row.update(updatedData);
        }
      });
    },
    
    refreshGrid() {
      if (this.table) {
        const columns = this.generateColumns();
        this.table.setColumns(columns);
        
        // Update data to ensure groupKey fields are properly populated
        const currentData = this.table.getData();
        const preparedData = this.prepareDataForDisplay(currentData);
        this.table.setData(preparedData);
      }
    },
    addRow() {
      if (this.table?.getDataCount() >= this.maxRowLimit) {
        this.$toast.error(
          this.$t("pages.batch_processing_table.max_limit_reached")
        );
        return;
      }

      const ids = this.rowData.map((r) => r.id);
      const newId = ids.length ? Math.max(...ids) + 1 : 1;
      const emptyRow = { id: newId };

      this.columnMeta.forEach((col) => {
        if (!(col.field in emptyRow)) {
          emptyRow[col.field] = col.type === "boolean" ? false : "";
        }
      });
      this.table.addRow(emptyRow);
      this.rowData.push(emptyRow);
    },
    copyGrid() {
      this.table.copyToClipboard();
    },
    clearGrid() {
      this.table.clearData();
    },
    downloadXLSX() {
      const originalData = this.table.getData();
      const originalColumns = this.table.getColumnDefinitions();

      const cleanedData = originalData
        .filter((row) => {
          return Object.values(row).some((val) => {
            if (val === null || val === undefined || val === "") return false;
            if (Array.isArray(val) && val.length === 0) return false;
            if (typeof val === "string" && val.trim() === "") return false;
            return true;
          });
        })
        .map((row, rowIndex) => {
          const cleaned = {};
          const tableRow = this.table.getRows()[rowIndex];
          
          for (const key in row) {
            if (key === 'actions') continue; // Skip actions column
            
            let val = row[key];
            if (
              val === null ||
              val === undefined ||
              (Array.isArray(val) && val.length === 0)
            ) {
              cleaned[key] = "";
            } else {
              // Get displayed value from table cell
              if (tableRow) {
                try {
                  const cell = tableRow.getCell(key);
                  const element = cell.getElement();
                  const displayValue = element ? (element.textContent || element.innerText) : '';
                  cleaned[key] = displayValue.trim() || val;
                } catch (e) {
                  cleaned[key] = val;
                }
              } else {
                cleaned[key] = val;
              }
            }
          }
          return cleaned;
        });

      const cleanedColumns = originalColumns
        .filter(col => col.field !== 'actions') // Remove actions column
        .map((col) => {
          const fieldName = col.field;
          const columnMeta = this.columnMeta.find(meta => 
            (meta.groupKey && meta.groupKey === fieldName) || meta.field === fieldName
          );
          
          return {
            ...col,
            title: columnMeta?.titles ? 
              (columnMeta.required ? `${columnMeta.titles} *` : columnMeta.titles) :
              (typeof col.title === "string"
                ? col.title
                    .replace(/<[^>]*>/g, "")
                    .trim()
                : col.title),
          };
        });
      this.table.setColumns(cleanedColumns);
      this.table.replaceData(cleanedData);
      this.table.download("xlsx", "data.xlsx");
      this.table.setColumns(originalColumns);
      this.table.replaceData(originalData);
    },
    checkIsRequiredError() {
      try {
        let errors = [];
        const tableColumns = this.table.getColumns().map(col => col.getField()).filter(field => field !== 'actions');
        const requiredFields = this.columnMeta.filter((m) => m.required && tableColumns.includes(m.groupKey || m.field));

        if (this.table.getRows().length === 0) return true;

        this.table.getRows().forEach((rowObj) => {
          const rowData = rowObj.getData();
          
          // Sort required fields by column order in table
          const sortedRequiredFields = requiredFields.sort((a, b) => {
            const fieldA = a.groupKey || a.field;
            const fieldB = b.groupKey || b.field;
            const indexA = tableColumns.indexOf(fieldA);
            const indexB = tableColumns.indexOf(fieldB);
            return indexA - indexB;
          });
          
          sortedRequiredFields.forEach((col) => {
            const fieldToCheck = col.groupKey || col.field;
            
            // Universal check for field editability
            let isFieldEditable = true;
            if (col.groupKey) {
              // For groupKey fields check if there's an active field
              const activeField = this.findActiveFieldForGroupKey(col.groupKey, rowData);
              isFieldEditable = !!activeField;
            } else if (col.editableIf) {
              // For regular fields check editableIf conditions
              const parentValue = rowData[col.editableIf.field];
              isFieldEditable = parentValue === col.editableIf.value;
            }
            
            // Only validate if field is editable
            if (!isFieldEditable) {
              return;
            }
            
            // Get the actual value to validate
            let valueToValidate;
            if (col.groupKey) {
              // For groupKey fields, we need to check the value in the active field
              const activeField = this.findActiveFieldForGroupKey(col.groupKey, rowData);
              if (activeField) {
                valueToValidate = rowData[activeField.field];
                
                // FALLBACK: If active field is empty but groupKey has value, use groupKey value
                if ((valueToValidate === undefined || valueToValidate === '' || valueToValidate === null) && 
                    rowData[col.groupKey] !== undefined && rowData[col.groupKey] !== '' && rowData[col.groupKey] !== null) {
                  valueToValidate = rowData[col.groupKey];
                }
              } else {
                // No active field, field should not be validated
                return;
              }
            } else {
              // For regular fields, check the field value directly
              valueToValidate = rowData[fieldToCheck];
            }
            
            const isEmpty =
              !valueToValidate ||
              (typeof valueToValidate === "string" && !valueToValidate.trim()) ||
              (Array.isArray(valueToValidate) && valueToValidate.length === 0);

            if (isEmpty) {
              try {
                const cell = rowObj.getCell(fieldToCheck);
                if (cell) {
                  const element = cell?.getElement?.();
                  if (element) {
                    element.style.background = "#ffeaea";
                  }
                  errors.push({
                    row: rowObj.getPosition(),
                    field: fieldToCheck,
                    title: col.titles || col.field,
                  });
                }
              } catch (cellErr) {
                console.warn("Could not highlight cell:", cellErr);
              }
            } else {
              try {
                const cell = rowObj.getCell(fieldToCheck);
                const element = cell?.getElement?.();
                if (element) {
                  element.style.background = "";
                }
              } catch (cellErr) {
                console.warn("Could not clear cell highlight:", cellErr);
              }
            }
          });
        });

        if (errors.length) {
          this.$toast.error(
            this.$t("pages.batch_processing_table.required_fields") +
              "\nFirst error: Row " +
              errors[0].row +
              ", Field: " +
              errors[0].title
          );
          return true;
        }
        return false;
      } catch (err) {
        console.error("err in checking required err", err);
        return true;
      }
    },
    async handleSave(whileSubmit = false) {
      if (!(this.checkPermission("batchProcessing.edit") || this.checkPermission("batchProcessing.create"))) return false;

      const rawData = this.table
        .getData()
        .filter((row) => Object.values(row).some((val) => val && val !== ""));
      // Process payload with groupKey structure
      const payload = rawData
        .map((row) => {
          const cleanedRow = {};

          Object.keys(row).forEach((key) => {
            let value = row[key];
            
            // Fix object values like {"":"Yes"} but preserve arrays (multiselect)
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              const keys = Object.keys(value);
              if (keys.length === 1) {
                value = value[keys[0]];
              } else {
                value = '';
              }
            }
            
            // Check if this field is a multiselect column
            const isMultiselect = this.columnMeta.some(meta => 
              ((meta.groupKey && meta.groupKey === key) || meta.field === key) &&
              meta.editor === "list" && 
              meta.editorParams?.multiselect
            );
            
            // Convert comma-separated strings to arrays for multiselect fields
            if (isMultiselect && typeof value === 'string' && value.trim() !== '') {
              if (value.includes(',')) {
                // Handle comma-separated values from Excel or manual entry
                value = value.split(',').map(v => v.trim()).filter(v => v !== '');
              } else {
                // Single value - convert to array
                value = [value.trim()];
              }
            }
            
            // Convert select field labels to values for all list editors
            const fieldMeta = this.columnMeta.find(meta => 
              (meta.groupKey && meta.groupKey === key) || meta.field === key
            );
            if (fieldMeta?.editor === "list" && !fieldMeta?.editorParams?.multiselect && value && value !== '') {
              if (fieldMeta.editorParams?.values) {
                const option = fieldMeta.editorParams.values.find(opt => opt.label === value);
                value = option ? option.value : value;
              }
            }
            
            if (
              value &&
              value !== "" &&
              value !== null &&
              value !== undefined
            ) {
              // Transform key to use groupKey for fields that have groupKey
              const keyMeta = this.columnMeta.find(meta => 
                (meta.groupKey && meta.groupKey === key) || meta.field === key
              );
              const finalKey = keyMeta?.groupKey ?? key;
              cleanedRow[finalKey] = value;
            } else if (Array.isArray(value) && value.length > 0) {
              // Preserve non-empty arrays (multiselect values)
              const keyMeta = this.columnMeta.find(meta => 
                (meta.groupKey && meta.groupKey === key) || meta.field === key
              );
              const finalKey = keyMeta?.groupKey ?? key;
              cleanedRow[finalKey] = value;
            }
          });

          return cleanedRow;
        })
        .filter((row) => Object.keys(row).length > 1); // Keep rows with more than just 'id'
      return await this.SaveTheTabularData(payload, whileSubmit);
    },

    async SaveTheTabularData(payload, whileSubmit) {
      this.saveLoader = true;
      try {
        const { data } = await BatchAxios.post(
          `/batch-processing/save-batch-excel-data/${this.getBatchId}`,
          {
            headers: this.columnMeta,
            data: payload,
          }
        );
        if (data?.success) {
          if (!whileSubmit) {
            this.$toast.success(
              this.$t("pages.batch_processing_table.draft_saved_success")
            );
          }
          return true;
        }
        return false;
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.detail || this.$t(`general.default_error`)
        );
        return false;
      } finally {
        this.saveLoader = false;
      }
    },

    async submitTheData() {
      if (!(this.checkPermission("batchProcessing.edit") || this.checkPermission("batchProcessing.create"))) return;

      this.submitLoadig = true;
      try {
        if (this.checkIsRequiredError()) return;

        const saved = await this.handleSave(true);
        if (saved) {
          const { data } = await BatchAxios.post(
            `/batch-processing/submit_batch_with_data/${this.getBatchId}`
          );
          if (data?.success) {
            this.$toast.success(
              this.$t("pages.batch_processing_table.batch_created_success")
            );
            this.$router.push({ path: "/batch-processing" });
          }
        }
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.detail || this.$t(`general.default_error`)
        );
      } finally {
        this.submitLoadig = false;
      }
    },

    // Sort groupKeys to process parent fields first
    sortGroupKeysByDependencies(groupKeys) {
      // Create a map of dependencies
      const dependencyMap = new Map();
      
      groupKeys.forEach(groupKey => {
        const fieldsWithGroupKey = this.columnMeta.filter(c => c.groupKey === groupKey);
        
        // Find if any field with this groupKey has a parentField
        const dependencies = fieldsWithGroupKey
          .map(field => field.parentField)
          .filter(parent => parent && groupKeys.includes(parent));
        
        dependencyMap.set(groupKey, dependencies);
      });
      
      // Topological sort
      const sorted = [];
      const visited = new Set();
      const visiting = new Set();
      
      const visit = (groupKey) => {
        if (visiting.has(groupKey)) {
          console.warn(`Circular dependency detected for groupKey: ${groupKey}`);
          return;
        }
        if (visited.has(groupKey)) return;
        
        visiting.add(groupKey);
        
        // Visit dependencies first
        const dependencies = dependencyMap.get(groupKey) || [];
        dependencies.forEach(dep => {
          if (groupKeys.includes(dep)) {
            visit(dep);
          }
        });
        
        visiting.delete(groupKey);
        visited.add(groupKey);
        sorted.push(groupKey);
      };
      
      groupKeys.forEach(groupKey => visit(groupKey));
      
      return sorted;
    },

    // Map Excel values to system values by checking available options
    mapExcelValueToSystemValue(fieldName, excelValue, rowData) {
      // Find the field metadata for this fieldName (could be groupKey or regular field)
      const fieldMeta = this.columnMeta.find(meta => 
        meta.field === fieldName || meta.groupKey === fieldName
      );
      
      if (!fieldMeta) return excelValue;
      
      // Get editor params for this field
      let editorParams = fieldMeta.editorParams;
      
      // If it's a function (dynamic params), call it with the row context
      if (typeof editorParams === 'function') {
        try {
          editorParams = editorParams({ getRow: () => ({ getData: () => rowData }) });
        } catch (e) {
          console.warn(`Failed to get dynamic editor params for ${fieldName}:`, e);
          return excelValue;
        }
      }
      
      // Check if this field has values to match against
      if (!editorParams?.values || !Array.isArray(editorParams.values)) {
        return excelValue;
      }
      
      const availableOptions = editorParams.values;
      
      // Try different matching strategies
      let matchingOption = null;
      
      // 1. Exact label match
      matchingOption = availableOptions.find(option => option.label === excelValue);
      
      // 2. Exact value match
      if (!matchingOption) {
        matchingOption = availableOptions.find(option => option.value === excelValue);
      }
      
      // 3. Case-insensitive label match
      if (!matchingOption) {
        matchingOption = availableOptions.find(option => 
          option.label?.toLowerCase() === excelValue.toLowerCase()
        );
      }
      
      // 4. Partial match - Excel label contains system label
      if (!matchingOption) {
        matchingOption = availableOptions.find(option => 
          excelValue.toLowerCase().includes(option.label?.toLowerCase())
        );
      }
      
      // 5. Reverse partial match - system label contains Excel value
      if (!matchingOption) {
        matchingOption = availableOptions.find(option => 
          option.label?.toLowerCase().includes(excelValue.toLowerCase())
        );
      }
      
      if (matchingOption) {
        return matchingOption.value;
      }
      
      return excelValue;
    },

    async refreshTheGrid() {
      await this.fetchBatchTabularData();
      this.initGrid();
    },
  },
};
</script>

<style>
.modal-overlay {
  position: fixed;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 10;
}

.modal-content {
  background: #fff;
  padding: 24px 32px 22px 32px;
  max-width: 510px;
  margin: 70px auto;
  border-radius: 10px;
  box-shadow: 0 8px 36px #0003;
}

.tabulator {
  height: 569px !important;
  border-radius: 8px;
}

/* .tabulator-header-contents {
    background-color:  var(--color-primary) !important;
} */
.tabulator .tabulator-header {
  /* background-color: var(--color-primary) !important; */
  /* color: white; */
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.tabulator .tabulator-header .tabulator-col {
  background-color: var(--theme-color-main) !important;
  color: white;
}

.tabulator .tabulator-tableholder {
  background-color: white;
}

.tabulator-custom-required-asterisk {
  color: #e74c3c;
  margin-left: 2px;
  font-weight: bold;
  font-size: 1.1em;
}

.view-mode .tabulator-cell {
  cursor: not-allowed !important;
}

.tabulator .tabulator-cell.tabulator-editing {
  cursor: text !important;
}
</style>
